import { StatusBar, StatusBarStyle } from 'expo-status-bar'
import { observer } from 'mobx-react-lite'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ActivityIndicator, ScrollView, StyleSheet, Text, View } from 'react-native'
import Animated, { FadeInDown } from 'react-native-reanimated'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { BottomSheetModal } from '@gorhom/bottom-sheet'

import { Streaks } from '../../components'
import { CallToActionReferCard } from '../../components/ANewDesign/CallToActionCard/CallToActionRefer'
import { MainHeader } from '../../components/MainHeader/MainHeader'
import ScheduleSection from '../../components/ProfileScheduleSection/ScheduleSection'
import { DS } from '../../design-system'
import useFetchTeachingTables from '../../hooks/useFetchTeachingTables'
import { useStore } from '../../models'
import { CompleteYourProfile } from './CompleteYourProfile'
import { DeleteAccountSheetModal } from './DeleteAccountSheetModal'
import { EditProfileSheet } from './EditProfileSheet'
import { EmptyProfile } from './EmptyProfile'
import Menu from './Menu.png'
import { PlayerName } from './PlayerName'
//import Daily10History from "@/components/Mini/d10/Daily10History"
//import CthHistory from "@/components/Mini/cth/CthHistory"
import { Daily10History } from './Daily10History'
import { CthHistory } from './CthHistory'
import { PocketPickHistory } from './PocketPickHistory'
import { BadgesSummaryCard } from '../badges-screen/BadgesSummaryCard'
import { DiamondsSummaryCard } from '@/screens/diamonds/components/DiamondsSummaryCard'
import { events } from '../../models/analytics/analytics-events'
import { PlayerBestHand, PlayerStats } from './PlayerStats'
import { useRemoteConfig } from '../../../module/remote-config/useRemoteConfig'
import { useThrottledCallback } from 'use-debounce'
import { useIsInGame } from '../../hooks/useIsInGame'
import { useFocusEffect } from 'expo-router'
import { useIsFocused } from '@react-navigation/native'
import { useDrawerStore } from '@/components/Drawer/Drawer'
import { useQuery } from 'convex/react'
import { api } from '@ppwr/convex/_generated/api'
import { formatPlayDate } from '../../utils/dateFormatter'

interface ContentProps {
  loading: boolean
  isEmpty: boolean
}

const Content: React.FC<ContentProps> = observer(({ loading, isEmpty }) => {
  const {
    session: { isAdmin, isSuperAdmin, user }
  } = useStore()
  const userId = user?.auth0Id || 'guest'
  const todayDate = formatPlayDate()

  // Same subscriptions as the minigame history cards, so Convex dedupes them
  const daily10History = useQuery(api.games.queries.getDaily10History, { userId, todayDate })
  const cthStats = useQuery(api.cth.queries.getCthProfileStats, { userId, todayDate })
  const pocketPickStats = useQuery(api.pocketPick.queries.getPocketPickProfileStats, { userId, todayDate })
  const minigamesLoading = daily10History === undefined || cthStats === undefined || pocketPickStats === undefined
  const hasMinigameStats =
    !!daily10History || (cthStats?.gamesPlayed ?? 0) > 0 || (pocketPickStats?.gamesPlayed ?? 0) > 0

  if (loading && isEmpty) {
    return (
      <View style={contentStyles.loadingContainer}>
        <ActivityIndicator size="large" color={DS.palette['Brand/Maroon']} style={contentStyles.loading} />
      </View>
    )
  }



  return (
    <>
      <View style={contentStyles.container}>
        {/* NOTE: hiding this for now
        @TODO: add back when we add teaching tables */}
        {/* <ScheduleSection /> */}
        <DS.Text variant="Caption/Medium" uppercase>
          MY STREAK
        </DS.Text>
        <Streaks />
      </View>
      {!isEmpty ? <PlayerStats /> : !minigamesLoading && !hasMinigameStats ? <EmptyProfile /> : null}
      {(isAdmin || isSuperAdmin) && (
        <DiamondsSummaryCard title="Diamonds" analyticsEvent={events.diamonds.profile_row_tapped} />
      )}
      <BadgesSummaryCard title="Badges" analyticsEvent={events.badges.profile_row_tapped} /> 
      <Daily10History />
      <CthHistory />
      <PocketPickHistory />
      {!isEmpty && <PlayerBestHand />}
      <CallToActionReferCard />
    </>
  )
})

const contentStyles = StyleSheet.create({
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center'
  },
  loading: {
    width: 20,
    height: 20
  },
  container: {
    gap: DS.spacing[8]
  }
})

// Matches the Avatar size in PlayerName so the menu centers on that row
const AVATAR_ROW_HEIGHT = 50

export type ProfileParams = {
  inGame?: boolean
}

// type TProfileScreenProps = ProfileScreenInGameProps | ProfileScreenProps

export const ProfileScreen = observer(() => {
  const isFocused = useIsFocused()
  const insets = useSafeAreaInsets()
  const [loading, setLoading] = useState(false)
  const store = useStore()
  const { toggleDrawer } = useDrawerStore()
  const editProfileSheetModal = useRef<BottomSheetModal>(null)
  const {
    game: {
      myHandsStats: { totalNumberOfHands },
      getPlayerGlobalStats
    }
  } = store

  const { isInGame } = useIsInGame()

  // Convex stats for practice + private (reactive, uses existing ConvexProvider auth)
  const convexUserId = store.session.user?.auth0Id || store.session.user?._id?.toString()
  const convexStats = useQuery(
    api.playerStats.queries.getPlayerConvexStats,
    convexUserId ? { userId: convexUserId } : 'skip'
  )

  const isEmpty = Number(totalNumberOfHands) === 0
  const needsCompleteProfile = store.session.needsCompleteProfile

  const handleEditClick = useThrottledCallback(() => {
    editProfileSheetModal.current?.present()
  }, 250)

  const getPlayerOverAllStats = useCallback(() => {
    const request = async () => {
      try {
        if (isEmpty) {
          setLoading(true)
        }
        await getPlayerGlobalStats(convexStats ?? undefined)
      } catch (error) {
        console.error('Get Player Games Error: ', error)
      } finally {
        setLoading(false)
      }
    }
    request()
  }, [isEmpty, convexStats, getPlayerGlobalStats])

  useFocusEffect(getPlayerOverAllStats)

  //useFetchTeachingTables()

  return (
    <View style={[styles.container]}>
      {isFocused ? <StatusBar style={'light' as StatusBarStyle} /> : null}
      <EditProfileSheet ref={editProfileSheetModal} />
      <DeleteAccountSheetModal />

      <View style={[styles.headerContainer, { paddingTop: insets.top }]}>
        <MainHeader
          headerContainerStyle={{
            zIndex: 2,
            position: 'absolute',
            top: insets.top + DS.spacing[16],
            height: AVATAR_ROW_HEIGHT,
            alignItems: 'center'
          }}
          hideLogo
          hideLeftDefaultIcon
          rightIcon={isInGame ? null : Menu}
          rightPress={() => !isInGame && toggleDrawer()}
          showDarkKeyIcon={false}
          showDarkDiamondsText={false}
        />
        <View pointerEvents="box-none" style={{ zIndex: 3, paddingHorizontal: DS.spacing[24], paddingTop: DS.spacing[16] }}>
          <PlayerName onEditPress={handleEditClick} />
        </View>
      </View>

      <ScrollView contentContainerStyle={[styles.scrollContainer, containerStyles.webWrapper]}>
        {needsCompleteProfile && <CompleteYourProfile />}
        <Content isEmpty={isEmpty} loading={loading} />
      </ScrollView>
    </View>
  )
})

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: DS.palette['Brand/Cream']
  },
  headerContainer: { backgroundColor: DS.palette['Brand/Maroon'] },
  scrollContainer: {
    padding: DS.spacing[24],
    gap: DS.spacing[24]
  },
  contentWrapper: {
    flexGrow: 1
  }
})

const containerStyles = StyleSheet.create({
  webWrapper: {
    width: '100%',
    maxWidth: 600,
    alignSelf: 'center'
  }
})
